/**
 * `/bank` — offene und mehrdeutige Buchungen zuordnen. **Nur admin.**
 *
 * Warum die Rolle: Wer diese Seite sieht, sieht alle Kontoumsaetze des Hauses
 * — Gehaelter, Privatentnahmen, alles. Der Ablauf schliesst aus, dass der
 * erste Mitarbeitende das sieht (§15.2). Der Router prueft es serverseitig
 * (`AdminUser`); diese Pruefung hier ist die freundliche Auskunft davor,
 * nicht der Schutz.
 *
 * Bewusst gekuerzt (§15.3): kein Kontoauszug zum Blaettern, keine Statistik.
 * Nur die Buchungen, bei denen ein Mensch etwas entscheiden muss.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Landmark } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import { MATCH_STATUS_LABEL } from '@/lib/beleg';
import { alterText, datum, datumZeit, euro, iban as ibanFormat } from '@/lib/format';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Seitenkopf } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band, Fehler, Laden, Leer } from '@/components/ui/Zustaende';
import type { BankTransactionResponse } from '@/types';

export function BankPage() {
  const { user } = useAuth();
  const cache = useQueryClient();
  const toast = useToast();
  const [nurOffen, setNurOffen] = useState(true);

  const istAdmin = user?.role === 'admin';

  const konten = useQuery({
    queryKey: ['bank-konten'],
    queryFn: () => bh.bankKonten(),
    enabled: istAdmin,
  });

  const umsaetze = useQuery({
    queryKey: ['bank-umsaetze', nurOffen],
    queryFn: () => bh.bankUmsaetze(nurOffen),
    enabled: istAdmin,
  });

  const zuordnen = useMutation({
    mutationFn: ({ id, document_id }: { id: string; document_id: string | null }) =>
      bh.bankZuordnen(id, { document_id }),
    onSuccess: (_neu, variablen) => {
      void cache.invalidateQueries({ queryKey: ['bank-umsaetze'] });
      toast.erfolg(
        variablen.document_id ? 'Buchung zugeordnet.' : 'Buchung als „passt nicht" abgelegt.',
      );
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  if (!istAdmin) {
    return (
      <div>
        <Seitenkopf titel="Bank" />
        <Band farbe="warnung">
          Diese Seite ist Admins vorbehalten. Hier stehen alle Kontoumsätze — auch Gehälter und
          Privates. Wer sie braucht, bekommt die Rolle von Henri.
        </Band>
      </div>
    );
  }

  return (
    <div>
      <Seitenkopf
        titel="Bank"
        beschreibung="Offene und mehrdeutige Buchungen zuordnen. Alles, was eindeutig war, hat das System schon abgehakt."
      >
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={nurOffen}
            onChange={(e) => setNurOffen(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          />
          nur offene
        </label>
      </Seitenkopf>

      {konten.data?.map((konto) => (
        <div
          key={konto.id}
          className="mb-4 flex flex-wrap items-center gap-6 rounded-xl border border-slate-200 bg-white px-5 py-4"
        >
          <Landmark size={18} className="text-slate-400" />
          <div>
            <p className="text-sm font-medium text-slate-900">{konto.name}</p>
            <p className="font-mono text-xs text-slate-500">{ibanFormat(konto.iban)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Letzter Abruf</p>
            <p className="text-sm text-slate-700">{datumZeit(konto.last_fetch_at)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Jüngster Auszug</p>
            {/* Fuenf Bankarbeitstage ohne Auszug meldet der Heartbeat — auch
                wenn jeder Abruf „erfolgreich" war: ohne fintech-Lizenz meldet
                er „keine neuen Umsätze" (§15.2). */}
            <p
              className={clsx(
                'text-sm',
                (konto.tage_ohne_auszug ?? 0) >= 5
                  ? 'font-medium text-danger-700'
                  : 'text-slate-700',
              )}
            >
              {datum(konto.last_statement_date)}
              {konto.tage_ohne_auszug !== null && konto.tage_ohne_auszug !== undefined && (
                <span className="ml-1.5 text-xs text-slate-400">
                  ({konto.tage_ohne_auszug} Bankarbeitstage her)
                </span>
              )}
            </p>
          </div>
        </div>
      ))}

      {konten.isError && <Fehler text={fehlerText(konten.error)} />}

      {umsaetze.isLoading && <Laden />}
      {umsaetze.isError && (
        <Fehler text={fehlerText(umsaetze.error)} onErneut={() => void umsaetze.refetch()} />
      )}

      {umsaetze.data && umsaetze.data.items.length === 0 && (
        <Leer
          titel="Nichts offen."
          hinweis="Jede Buchung ist einem Beleg zugeordnet oder bewusst beiseitegelegt."
        />
      )}

      {umsaetze.data && umsaetze.data.items.length > 0 && (
        <>
          <p className="mb-3 text-sm text-slate-500">
            {umsaetze.data.offen} offen · {umsaetze.data.mehrdeutig} mehrdeutig
          </p>
          <div className="space-y-3">
            {umsaetze.data.items.map((umsatz) => (
              <Buchung
                key={umsatz.id}
                umsatz={umsatz}
                laeuft={zuordnen.isPending}
                onZuordnen={(document_id) => zuordnen.mutate({ id: umsatz.id, document_id })}
              />
            ))}
          </div>
        </>
      )}

      <p className="mt-6 text-xs text-slate-400">
        Zuletzt geprüft {alterText(0.1)} · Umsätze kommen per EBICS, Abruf und Zuordnung laufen
        ohne Zutun.
      </p>
    </div>
  );
}

function Buchung({
  umsatz,
  laeuft,
  onZuordnen,
}: {
  umsatz: BankTransactionResponse;
  laeuft: boolean;
  onZuordnen: (document_id: string | null) => void;
}) {
  const abfluss = Number(umsatz.amount) < 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[16rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-900">
              {umsatz.counterparty_name ?? 'ohne Namen'}
            </span>
            <Chip
              klassen={
                umsatz.match_status === 'offen'
                  ? 'border-warning-200 bg-warning-50 text-warning-700'
                  : umsatz.match_status === 'zugeordnet'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : undefined
              }
            >
              {MATCH_STATUS_LABEL[umsatz.match_status]}
            </Chip>
          </div>
          <p className="mt-0.5 text-sm text-slate-500">{umsatz.purpose ?? 'kein Verwendungszweck'}</p>
          <p className="mt-0.5 text-xs text-slate-400">
            gebucht {datum(umsatz.booking_date)}
            {umsatz.counterparty_iban && <> · {ibanFormat(umsatz.counterparty_iban)}</>}
            {umsatz.mandate_reference && <> · Mandat {umsatz.mandate_reference}</>}
          </p>
        </div>
        <p
          className={clsx(
            'shrink-0 text-right tabular-nums font-semibold',
            abfluss ? 'text-slate-900' : 'text-success-600',
          )}
        >
          {euro(umsatz.amount)}
        </p>
      </div>

      {umsatz.document_bezeichnung && (
        <p className="mt-2 text-sm text-slate-600">
          Zugeordnet: <span className="text-slate-900">{umsatz.document_bezeichnung}</span>
        </p>
      )}

      {umsatz.vorschlaege.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            {umsatz.vorschlaege.length === 1 ? 'Vorschlag' : 'Vorschläge'}
          </p>
          {umsatz.vorschlaege.map((vorschlag) => (
            <div
              key={vorschlag.document_id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-800">{vorschlag.bezeichnung}</p>
                <p className="text-xs text-slate-500">
                  gefunden über {GRUND_TEXT[vorschlag.grund]} · Zuversicht {vorschlag.zuversicht}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={laeuft}
                onClick={() => onZuordnen(vorschlag.document_id)}
              >
                Bestätigen
              </Button>
            </div>
          ))}
        </div>
      )}

      {(umsatz.match_status === 'offen' || umsatz.match_status === 'vorschlag') && (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" disabled={laeuft} onClick={() => onZuordnen(null)}>
            {umsatz.vorschlaege.length > 0 ? 'Keiner passt' : 'Beiseitelegen'}
          </Button>
        </div>
      )}
    </div>
  );
}

const GRUND_TEXT: Record<string, string> = {
  betrag_iban: 'Betrag und IBAN',
  verwendungszweck: 'den Verwendungszweck',
  belegnummer: 'die Belegnummer',
  mandat: 'die Mandatsreferenz',
};
